/**
 * Renders an expense's coding (GL account or Job + Budget Item) as stacked rows.
 * Each row shows the GL/Job-BudgetItem on line 1 and the per-line memo on line 2.
 * - Splits: each split → one row group.
 * - Single line: the top-level coding → one row group.
 */
export function CodingCell({ exp, compact = false }) {
  const rows = exp.splits?.length > 0
    ? exp.splits.map((s, i) => ({
        _key: s._uid || `${exp.id}-split-${i}-${s.job_id || s.gl_account_id || ""}-${s.budget_item_id || ""}`,
        type: s.type,
        job: s.job_name || s.job_id || "",
        budget: s.budget_item_name || "",
        gl: s.gl_account_name || s.gl_account_id || "",
        description: s.description || "",
      }))
    : [{
        _key: `${exp.id}-single`,
        type: exp.expense_type,
        job: exp.job_name || "",
        budget: exp.budget_item_name || "",
        gl: exp.gl_account_name || "",
        description: exp.line_description || "",
      }];

  const primarySize = compact ? "text-[11px]" : "text-xs";
  const memoSize = compact ? "text-[10px]" : "text-[11px]";

  return (
    <div className="space-y-1" data-testid="coding-cell">
      {rows.map((r) => {
        const isJob = r.type === "job" || (!r.type && r.job);
        const primary = isJob ? (
          <span className="break-words leading-snug">
            <span className="font-medium text-zinc-700">{r.job || "—"}</span>
            {r.budget && <span className="text-zinc-500"> — {r.budget}</span>}
          </span>
        ) : (
          <span className="font-medium text-zinc-700 break-words leading-snug">{r.gl || "—"}</span>
        );
        const title = isJob
          ? `${r.job}${r.budget ? " — " + r.budget : ""}${r.description ? "\n" + r.description : ""}`
          : `${r.gl}${r.description ? "\n" + r.description : ""}`;
        return (
          <div key={r._key} className={primarySize} title={title}>
            <p>{primary}</p>
            {r.description && (
              <p className={`${memoSize} text-zinc-400 italic break-words leading-tight mt-0.5`}>{r.description}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}
